import { Table, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { IconButtonDelete } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonDelete';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { TextButton } from '@components/core-ui/form-elements/buttons/Button/variations/TextButton';
import { CheckBox } from '@components/core-ui/form-elements/CheckBox';
import TrashBin from '@components/icons/TrashBin.svg';
import { CustomConfirmationModal } from '@modals/common/confirmations/CustomConfirmationModal';
import { ModalFooterEnd } from '@modals/core-ui/ModalFooter/variations/ModalFooterEnd';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { getAmericanTime } from '@helpers/getAmericanTimezone';
import { useAPI } from '@providers/useAPI';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PaymentMethodResponseDto, PaymentService } from 'app/API';
import { ApiCallError } from 'app/http/ApiCallError';
import { billingKeys } from 'app/queries/billing/billingKeys';
import { createFetchMemberPaymentMethodsQueryFn } from 'app/queries/billing/createFetchMemberPaymentMethodsQueryFn';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { getPaymentMethodsLoadErrorMessage } from './helpers/getPaymentMethodsLoadErrorMessage';
import { Content } from './styles/Content';

type PaymentMethodResponseDtoWithClassName = PaymentMethodResponseDto & {
  className?: string;
};

export const PaymentMethodsHistory = ({
  billingAccountId,
  onCancel,
}: {
  billingAccountId: string;
  onCancel: () => void;
}) => {
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const [deletePaymentMethod, setDeletePaymentMethod] = useState<PaymentMethodResponseDtoWithClassName>();
  const paymentMethodsKey = billingKeys.memberPaymentMethodsByAccount(billingAccountId);

  const paymentMethodsQuery = useQuery({
    queryKey: paymentMethodsKey,
    queryFn: createFetchMemberPaymentMethodsQueryFn(call),
  });
  const paymentMethods: PaymentMethodResponseDtoWithClassName[] = (paymentMethodsQuery.data ?? []).map((item) =>
    item.default ? { ...item, className: 'defaultMethod' } : item,
  );
  const hasLoadError = paymentMethodsQuery.isError;

  useEffect(() => {
    if (!paymentMethodsQuery.error) {
      return;
    }
    const message = getPaymentMethodsLoadErrorMessage(paymentMethodsQuery.error);
    if (message) {
      toast.error(message);
    }
  }, [paymentMethodsQuery.error]);

  const notifyUpdateError = (error: unknown) => {
    if (error instanceof ApiCallError && error.status === 401) {
      return;
    }
    toast.error('Unable to update payment methods. Please try again.');
  };

  const refreshPaymentMethods = (nextPaymentMethods: PaymentMethodResponseDto[]) => {
    queryClient.setQueryData(paymentMethodsKey, nextPaymentMethods);
    void queryClient.invalidateQueries({ queryKey: billingKeys.paymentMethodsByAccount(billingAccountId) });
  };

  const cancelPaymentMethodMutation = useMutation({
    mutationFn: async (paymentMethod: PaymentMethodResponseDto) => {
      await call(() =>
        PaymentService.billingAccountCancelPaymentMethod({
          baId: billingAccountId,
          paymentMethodId: paymentMethod.id,
        }),
      );
      setDeletePaymentMethod(undefined);
      const newPaymentMethods = await call(() =>
        PaymentService.billingAccountPaymentMethodList({ baId: billingAccountId }),
      );
      if (paymentMethod.default && newPaymentMethods[0]) {
        await call(() =>
          PaymentService.billingAccountSetDefaultPaymentMethod({
            baId: billingAccountId,
            paymentMethodId: newPaymentMethods[0].id,
          }),
        );
        return newPaymentMethods.map((method, index) => (index === 0 ? { ...method, default: true } : method));
      }
      return newPaymentMethods;
    },
    onSuccess: (nextPaymentMethods) => {
      queryClient.setQueryData(paymentMethodsKey, nextPaymentMethods);
    },
    onError: notifyUpdateError,
    onSettled: () => queryClient.invalidateQueries({ queryKey: billingKeys.paymentMethodsByAccount(billingAccountId) }),
  });

  const setDefaultPaymentMethodMutation = useMutation({
    mutationFn: (paymentMethodId: string) =>
      call(() =>
        PaymentService.billingAccountSetDefaultPaymentMethod({
          baId: billingAccountId,
          paymentMethodId,
        }),
      ),
    onSuccess: (_, paymentMethodId) => {
      refreshPaymentMethods(
        (paymentMethodsQuery.data ?? []).map((method) => ({
          ...method,
          default: method.id === paymentMethodId,
        })),
      );
    },
    onError: notifyUpdateError,
  });

  const handleSetDefaultPaymentMethod = (paymentMethodId: string) => {
    if (!paymentMethods.some((method) => method.id === paymentMethodId)) {
      return;
    }
    setDefaultPaymentMethodMutation.mutate(paymentMethodId);
  };

  const isLoading =
    (paymentMethodsQuery.isFetching && (paymentMethodsQuery.data === undefined || paymentMethodsQuery.isError)) ||
    cancelPaymentMethodMutation.isPending ||
    setDefaultPaymentMethodMutation.isPending;

  const deletePaymentMethodDescription = `Are you sure you want to delete ${
    deletePaymentMethod?.expDate?.month
  }/${String(deletePaymentMethod?.expDate?.year).substring(2, 4)} **** ${deletePaymentMethod?.last4} card from history?`;

  if (deletePaymentMethod) {
    return (
      <CustomConfirmationModal
        description={deletePaymentMethodDescription}
        buttonText="Delete"
        isSubmitting={isLoading}
        onCancel={() => setDeletePaymentMethod(undefined)}
        onSuccess={() => cancelPaymentMethodMutation.mutate(deletePaymentMethod)}
      />
    );
  }

  return (
    <ModalWrapper width="600px">
      {isLoading ? <AbsoluteSpinner overlay={true} /> : null}
      <ModalTitle>Payment Method History</ModalTitle>
      <Content>
        <div className="tableWrapper">
          <Table>
            <Thead $white>
              <Tr>
                <Th className="!text-center">TYPE</Th>
                <Th>CREATED</Th>
                <Th>LAST 4 DIGITS</Th>
                <Th>DEFAULT</Th>
                <Th></Th>
              </Tr>
            </Thead>
            <Tbody>
              {paymentMethods.map((data, index) => (
                <Tr key={data.id || index} className={data.className}>
                  <Td>
                    <p className="active">{data.type}</p>
                  </Td>
                  <Td>
                    <p>{getAmericanTime(Number(data.created) * 1000)}</p>
                  </Td>
                  <Td>
                    <p>**** {data.last4}</p>
                  </Td>
                  <Td>
                    <div className="centerText">
                      <CheckBox
                        name={`default-method-${data.id}`}
                        rounded={true}
                        onChange={() => handleSetDefaultPaymentMethod(data.id)}
                        checked={data.default}
                        disabled={false} // TODO: change logic here with new permissions on Organizations for Billing Accounts
                      />
                    </div>
                  </Td>
                  <Td>
                    <IconButtonDelete
                      onClick={() => {
                        setDeletePaymentMethod(data);
                      }}
                      disabled={false} // TODO: change logic here with new permissions on Organizations for Billing Accounts
                    >
                      <TrashBin />
                    </IconButtonDelete>
                  </Td>
                </Tr>
              ))}
              {!isLoading && hasLoadError ? (
                <Tr className="no-border">
                  <Td colSpan={5} className="!max-w-none">
                    <div className="flex items-center justify-center gap-1">
                      <span className="text-sm">Unable to load payment methods.</span>
                      <TextButton className="!px-0" onClick={() => void paymentMethodsQuery.refetch()}>
                        Try again
                      </TextButton>
                    </div>
                  </Td>
                </Tr>
              ) : null}
              {!isLoading && !hasLoadError && paymentMethods.length === 0 ? (
                <Tr className="no-border">
                  <Td colSpan={5} className="!max-w-none text-center text-sm text-foreground-muted">
                    No payment methods yet.
                  </Td>
                </Tr>
              ) : null}
            </Tbody>
          </Table>
        </div>
        <ModalFooterEnd>
          <OutlinedButton onClick={() => onCancel()}>Cancel</OutlinedButton>
        </ModalFooterEnd>
      </Content>
    </ModalWrapper>
  );
};
